import React, { useState, useEffect } from 'react';
import { Bell, X, ChevronRight, ChevronLeft, Megaphone, Info, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Public announcements that show even when not logged in
const PUBLIC_ANNOUNCEMENTS = [
  {
    id: 'welcome',
    type: 'info',
    title: 'Welcome to Beskar Security Platform',
    message: 'Experience enterprise-grade security and compliance tools. Sign up today to get started!',
    link: '/login',
    linkText: 'Get Started',
    dismissible: true
  },
  {
    id: 'osint-free',
    type: 'feature',
    title: 'Free OSINT Tools Available',
    message: 'Access IOC Analyzer, Domain Monitoring, and more with 17+ threat intelligence sources.',
    link: '/login',
    linkText: 'Try Now',
    dismissible: true
  }
];

// Popup notification for public (unauthenticated) users
// DISABLED BY DEFAULT - Set ENABLE_PUBLIC_ANNOUNCEMENTS=true in env to enable
export const PublicNotificationPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [dismissedIds, setDismissedIds] = useState([]);

  useEffect(() => {
    // Check if public announcements are enabled (disabled by default)
    const enableAnnouncements = process.env.REACT_APP_ENABLE_PUBLIC_ANNOUNCEMENTS === 'true';
    if (!enableAnnouncements) {
      setIsVisible(false);
      return;
    }

    // Check if user is logged in - don't show popup for authenticated users
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token || user) {
      setIsVisible(false);
      return;
    }

    // Load dismissed announcements from localStorage
    const dismissed = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    setDismissedIds(dismissed);

    // Find first non-dismissed announcement
    const available = PUBLIC_ANNOUNCEMENTS.filter(a => !dismissed.includes(a.id));
    if (available.length > 0) {
      // Show popup after 2 seconds
      const timer = setTimeout(() => {
        // Double check user is still not logged in
        const tokenCheck = localStorage.getItem('token');
        const userCheck = localStorage.getItem('user');
        if (!tokenCheck && !userCheck) {
          setCurrentAnnouncement(available[0]);
          setIsVisible(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    if (currentAnnouncement) {
      const newDismissed = [...dismissedIds, currentAnnouncement.id];
      setDismissedIds(newDismissed);
      localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
    }
    setIsVisible(false);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'feature':
        return <Sparkles className="w-5 h-5 text-purple-400" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'announcement':
        return <Megaphone className="w-5 h-5 text-blue-400" />;
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  if (!isVisible || !currentAnnouncement) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-slideUp">
      <Card className="w-80 p-4 shadow-2xl bg-slate-900 border border-slate-700">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon(currentAnnouncement.type)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">
              {currentAnnouncement.title}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {currentAnnouncement.message}
            </p>
            {currentAnnouncement.link && (
              <a
                href={currentAnnouncement.link}
                className="inline-flex items-center gap-1 mt-2 text-xs text-blue-400 hover:text-blue-300"
              >
                {currentAnnouncement.linkText || 'Learn More'}
                <ChevronRight className="w-3 h-3" />
              </a>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 hover:bg-slate-800 rounded text-slate-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </Card>
    </div>
  );
};

// Collapsible notification sidebar for authenticated users
export const NotificationSidebar = ({ isOpen, onToggle }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      // In a real app, fetch from API
      // For now, use static demo notifications
      setNotifications([
        {
          id: '1',
          type: 'scan_complete',
          title: 'Vulnerability Scan Complete',
          message: 'Found 12 vulnerabilities in package-lock.json',
          timestamp: new Date().toISOString(),
          read: false
        },
        {
          id: '2',
          type: 'alert',
          title: 'New Critical CVE',
          message: 'CVE-2024-1234 affects your dependencies',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          read: false
        },
        {
          id: '3',
          type: 'info',
          title: 'Policy Review Due',
          message: 'Information Security Policy requires review',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          read: true
        }
      ]);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'scan_complete':
        return <Sparkles className="w-4 h-4 text-green-400" />;
      case 'alert':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      default:
        return <Info className="w-4 h-4 text-blue-400" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) {
      return `${Math.floor(diff / 60000)}m ago`;
    } else if (diff < 86400000) {
      return `${Math.floor(diff / 3600000)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="fixed top-4 right-4 z-40 p-2 bg-slate-800 hover:bg-slate-700 rounded-full shadow-lg transition-all"
        data-testid="notification-toggle"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-slate-900 border-l border-slate-700 shadow-2xl z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                {unreadCount} new
              </span>
            )}
          </h2>
          <button
            onClick={onToggle}
            className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto h-[calc(100vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-slate-800/50 cursor-pointer transition-colors ${
                    !notification.read ? 'bg-slate-800/30' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${notification.read ? 'text-slate-300' : 'text-white'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        {formatTime(notification.timestamp)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700 bg-slate-900">
          <Button variant="outline" className="w-full text-slate-300 border-slate-600" size="sm">
            Mark all as read
          </Button>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onToggle}
        />
      )}
    </>
  );
};

// Add CSS animation
const styleEl = document.createElement('style');
styleEl.textContent = `
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-slideUp {
    animation: slideUp 0.3s ease-out;
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('notification-styles')) {
  styleEl.id = 'notification-styles';
  document.head.appendChild(styleEl);
}

export default PublicNotificationPopup;
